/* eslint-disable no-return-assign */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Component } from 'preact';
import PropTypes from 'prop-types';
import IMask from 'imask';

import getUTM from '%/utils/utm';
import getGADParams from '%/utils/gadTracking';
import ItemCard from '../ItemCard';
import { priceMask } from '../../../utils/masks';
import { debounce } from '../../../utils/debounce';

import {
  setPersonalInformations,
  setModelInformations,
  setUsedModelValue,
  setUsedOnChange,
  setEntryValue,
  setEntryValueWithComplement,
  setFinancingValue,
  setInstallments,
  setFinancingOptions,
} from '../../../stores/FinancingSimulator';
import Tooltip from '../Tooltip';
import RangeInput from '../../RangeInput';
import {
  currencyStringToNumber,
  removeCurrencyFormat,
  addCurrencyDots,
} from '../utils';

export default class FinancingSimulation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      cpf: '',
      dateOfBirth: '',
      unit: '',
      model: '',
      versionName: '',
      modelYear: '',
      fabricationYear: '',
      modelThumb: '',
      modelValue: '',
      fipeCode: '',
      usedModelValue: '',
      usedOnChange: '',
      entryValue: '',
      entryValueWithComplement: '',
      defaultInstallmentsList: [
        { Prazo: 18 },
        { Prazo: 24 },
        { Prazo: 36 },
        { Prazo: 42 },
        { Prazo: 48 },
        { Prazo: 60 },
      ],
      installmentsData: [],
      installmentsList: [],
      installments: '',
      financingValue: null,
      financingSimulationError: null,
      financingSimulationIsLoading: false,
      entryValueIsValid: true,
      financingValueError: null,
    };

    this.handleEntryValueChange = this.handleEntryValueChange.bind(this);
    this.simulateFinancing = this.simulateFinancing.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.usedModelValueBigger = this.usedModelValueBigger.bind(this);
    this.entryRangeInputMaxValue = this.entryRangeInputMaxValue.bind(this);
    this.rangeInputMaxValueLabel = this.rangeInputMaxValueLabel.bind(this);
    this.entryInputDisabled = this.entryInputDisabled.bind(this);
    this.getTotalEntryValue = this.getTotalEntryValue.bind(this);

    window.store.FinancingSimulator.watch(setPersonalInformations, store => {
      this.setState({
        cpf: store.cpf,
        dateOfBirth: store.dateOfBirth,
      });
    });

    window.store.FinancingSimulator.watch(setModelInformations, store => {
      this.setState({
        unit: store.unit,
        model: store.model,
        versionName: store.versionName,
        modelYear: store.modelYear,
        fabricationYear: store.fabricationYear,
        modelThumb: store.modelThumb,
        modelValue: store.modelValue,
        fipeCode: store.fipeCode,
      });
    });

    window.store.FinancingSimulator.watch(setUsedModelValue, store => {
      this.setState({
        usedModelValue: store.usedModelValue,
        entryValueWithComplement: store.usedModelValue,
      });

      this.setState(prevState => ({
        financingValue: this.calculateFinancingValue(
          this.entryValueMask.unmaskedValue,
          prevState.usedModelValue,
          prevState.modelValue,
        ),
      }));

      this.handleEntryPercentageCalculation();
    });

    window.store.FinancingSimulator.watch(setUsedOnChange, store => {
      this.setState({
        usedOnChange: store.usedOnChange,
      });
    });
  }

  componentDidMount() {
    this.entryValueMask = IMask(this.entryValueInput, priceMask);

    this.getFinancingSimulationConditions();
  }

  handleEntryValueChange(value) {
    const { usedModelValue } = this.state;

    const entryValueConverted = currencyStringToNumber(value);

    const entryRangeInputMaxValue = Math.floor(this.entryRangeInputMaxValue());

    const entryValue =
      entryValueConverted > entryRangeInputMaxValue
        ? entryRangeInputMaxValue
        : entryValueConverted;

    const entryValueFormatted = addCurrencyDots(String(entryValue));

    const entryValueWithComplement = this.calculateEntryValueWithComplement(
      entryValueFormatted,
      usedModelValue,
    );

    this.setState(
      prevState => ({
        entryValue: entryValueFormatted,
        entryValueWithComplement,
        entryValueIsValid: true,
        financingValue: this.calculateFinancingValue(
          entryValueFormatted,
          prevState.usedModelValue,
          prevState.modelValue,
        ),
      }),
      () => {
        setEntryValue(entryValueFormatted);
        setEntryValueWithComplement(entryValueWithComplement);

        this.handleEntryPercentageCalculation();
      },
    );
  }

  handleNextStep(e, simulation) {
    const { data } = simulation;

    const installmentValue = data[0].ValorParcela;
    const financedValue = data[0].ValorBruto;
    const interestRate = data[0].TaxaNetAM;

    setFinancingOptions({ installmentValue, financedValue, interestRate });

    this.sendConversion();
    window.triggerNextStep({
      target: e.target,
      currentTarget: this.nextButton,
    });
  }

  handleValidation() {
    const formInputs = [
      {
        element: this.entryValueInput,
        stateKey: 'entryValueIsValid',
      },
    ];

    let formIsValid = true;

    formInputs.forEach(({ element, stateKey }) => {
      let { value } = element;
      value = value.trim();

      if (value === '' || value === 'R$') {
        this.setState({
          [stateKey]: false,
        });

        formIsValid = false;
      } else {
        this.setState({
          [stateKey]: true,
        });
      }
    });

    return formIsValid;
  }

  handleInstallmentsChange(installments) {
    this.setState({ installments });

    setInstallments(installments);
  }

  handleEntryPercentageCalculation() {
    debounce(() => {
      const entryPercentage = this.calculateEntryPercentage();

      this.getInstallmentsListByData(entryPercentage || 0);
    }, 500)();
  }

  setInstallmentsListFallback() {
    const { defaultInstallmentsList } = this.state;

    this.setState({ installmentsList: defaultInstallmentsList });
  }

  getInstallmentsListByData(entryPercentage) {
    const { installmentsData } = this.state;

    if (!installmentsData) return;

    const floored = Math.floor(entryPercentage / 10) * 10;

    const matchedPercent = Math.min(floored, 60);

    const match = installmentsData.find(
      option => option.PercentualEntrada === matchedPercent,
    );

    if (!match) return;

    const installments = match.ListaPrazos[0].Prazo;

    this.setState({
      installmentsList: match.ListaPrazos,
    });

    this.handleInstallmentsChange(installments);
  }

  getConversionParams() {
    const {
      model: product,
      version,
      unit,
      modelValue,
      username: name,
      phone,
      cpf,
      email,
      dateOfBirth,
      mailing,
      phoning,
      whatsapping,
      usedOnChange,
      usedModelBrand,
      usedModelName,
      usedModelYear,
      usedModelKM,
      usedModelFipeCode,
      usedModelValue: usedModelSaleValue,
      entryValue,
      entryValueWithComplement,
      installments,
      installmentValue,
      financingValue,
      productId,
      financedValue,
    } = window.store.FinancingSimulator.getState();

    const formattedDateOfBith = dateOfBirth.split('-').reverse().join('/');
    const formattedUsedModelSaleValue = this.formatValue(usedModelSaleValue);
    const formattedEntryValue = this.formatValue(entryValue);
    const formattedEntryValueWithComplement = this.formatValue(
      entryValueWithComplement,
    );
    const formattedInstallmentValue = this.maskedValue(installmentValue);
    const formattedFinancedValue = this.maskedValue(financedValue);

    return {
      bait: '-7',
      paymentMethod: 'Quero financiar',
      SubOrigem__c: 'DDM Financiamento',
      category: this.props.category,
      utmz: getUTM(),
      gad_tracking: getGADParams(),
      brand: this.props.brand,
      channel: this.props.channel,
      unit,
      product_id: productId,
      product,
      version,
      modelValue,
      cpf,
      dateOfBirth: formattedDateOfBith,
      email,
      name,
      mailing,
      phone,
      phoning,
      whatsapping,
      usedOnChange,
      usedModelBrand,
      usedModelName,
      usedModelYear,
      usedModelKM,
      usedModelFipeCode,
      usedModelSaleValue: formattedUsedModelSaleValue,
      entryValue: formattedEntryValue,
      entryValueWithComplement: formattedEntryValueWithComplement,
      installments,
      installmentValue: formattedInstallmentValue,
      financingValue,
      financedValue: formattedFinancedValue,
    };
  }

  getSimulationParams() {
    const {
      cpf,
      dateOfBirth,
      fabricationYear,
      modelYear,
      financingValue,
      installments,
      fipeCode,
      entryValueWithComplement,
    } = this.state;

    const formattedDateOfBirth = this.formatDate(dateOfBirth);
    const formattedCpf = this.formatCpf(cpf);

    return {
      valorSolicitado: financingValue,
      valorEntrada: entryValueWithComplement,
      quantidadeParcelas: installments,
      tipoVeiculo: 'Novo',
      anoFabricacao: fabricationYear,
      anoModelo: modelYear,
      codigoMarca: '',
      placaUFLicenciamento: 'SP',
      codigoTabelaMercado: fipeCode,
      tipoPessoa: 'FISICA',
      cpfcnpj: formattedCpf,
      dataNascimentoOuFundacao: formattedDateOfBirth,
      pcd: 'nao',
      uf: 'SP',
    };
  }

  async getFinancingSimulationConditions() {
    this.setInstallmentsListFallback();

    try {
      const result = await window.service.checkFinancingSimulationConditions();

      const { data } = result;

      const hasIstallmentsList = data && data.length > 0;
      const installmentsList = hasIstallmentsList ? data[0].ListaPrazos : [];
      const firstIntallments = installmentsList[0].Prazo;

      this.setState({ installmentsList, installmentsData: data });
      this.handleInstallmentsChange(firstIntallments);
    } catch (error) {
      throw new Error('Falha ao capturar as condições de financiamento');
    }
  }

  getTotalEntryValue() {
    const { entryValue, usedModelValue } = this.state;

    let totalEntry = 0;

    if (entryValue)
      totalEntry += parseInt(removeCurrencyFormat(entryValue), 10);

    if (usedModelValue)
      totalEntry += parseInt(removeCurrencyFormat(usedModelValue), 10);

    return this.maskedValue(totalEntry);
  }

  maskedValue(number) {
    return number.toLocaleString('pt-br', { minimumFractionDigits: 2 });
  }

  unmaskedValue(number) {
    const formatedNumber = Number(
      number.replace(/[^0-9,-]+/g, '').replace(',', '.'),
    );

    return formatedNumber;
  }

  calculateFinancingValue(entryValue, usedModelValue, modelValue) {
    const entryValueFormated = this.unmaskedValue(entryValue);
    const usedModelValueFormated = this.unmaskedValue(usedModelValue);
    const modelValueFormated = this.unmaskedValue(modelValue);

    let financingValue =
      modelValueFormated - (entryValueFormated + usedModelValueFormated);
    financingValue = financingValue > 0 ? financingValue : 0;

    setFinancingValue(this.maskedValue(financingValue));

    return financingValue;
  }

  calculateEntryValueWithComplement(entryValue, usedModelValue) {
    const entryValueFormated = this.unmaskedValue(entryValue);
    const usedModelValueFormated = this.unmaskedValue(usedModelValue);

    if (usedModelValueFormated === 0) {
      return entryValueFormated;
    }

    return entryValueFormated + usedModelValueFormated;
  }

  isNumber(value) {
    return typeof value === 'number';
  }

  calculateEntryPercentage() {
    const { entryValueWithComplement, modelValue } = this.state;

    const convertedEntryValueWithComplement = this.isNumber(
      entryValueWithComplement,
    )
      ? entryValueWithComplement
      : currencyStringToNumber(entryValueWithComplement);

    const convertedModelValue = currencyStringToNumber(modelValue);

    const entryPercentage =
      (convertedEntryValueWithComplement / convertedModelValue) * 100;

    return entryPercentage;
  }

  validateFinancingValue() {
    const { financingValue } = this.state;

    const financingValueIsValid = financingValue !== 0;

    this.setState({
      financingValueError: !financingValueIsValid,
      financingSimulationError: null,
    });

    return financingValueIsValid;
  }

  formatValue(number) {
    if (!number) return null;

    const convertedNumber = parseFloat(String(number).replace(/\D/g, ''));

    return this.maskedValue(convertedNumber);
  }

  formatDate(dateOfBirth) {
    return new Date(dateOfBirth).toISOString();
  }

  formatCpf(cpf) {
    return cpf.replaceAll('.', '').replace('-', '');
  }

  sendConversion() {
    const params = this.getConversionParams();

    return window.serviceConversion
      .convert(params)
      .then(() => {})
      .catch(error => {
        console.error(error);
      });
  }

  async simulateFinancing(e) {
    e.preventDefault();

    const formIsValid = this.handleValidation();
    const financingValueIsValid = this.validateFinancingValue();

    if (formIsValid && financingValueIsValid) {
      const simulationParams = this.getSimulationParams();

      try {
        this.setState({
          financingSimulationError: null,
          financingSimulationIsLoading: true,
        });

        const simulation = await window.service.executeFinancingSimulation(
          simulationParams,
        );

        this.handleNextStep(e, simulation);

        this.setState({
          financingSimulationIsLoading: false,
        });
      } catch (error) {
        this.setState({
          financingSimulationError: true,
          financingSimulationIsLoading: false,
        });

        console.error(error);
      }
    } else {
      this.setState({
        financingSimulationIsLoading: false,
      });
    }
  }

  /* verifica se o valor do veículo usado é menor que
  o valor do veículo simulado no financiamento. */
  usedModelValueBigger() {
    const { modelValue, usedModelValue } = this.state;

    return (
      currencyStringToNumber(usedModelValue) >
      currencyStringToNumber(modelValue)
    );
  }

  entryInputDisabled() {
    return this.usedModelValueBigger();
  }

  entryInputValue() {
    const { entryValue } = this.state;

    if (this.usedModelValueBigger()) return 'R$ 0';

    return entryValue ? `R$ ${entryValue}` : entryValue;
  }

  entryRangeInputValue() {
    if (this.usedModelValueBigger()) return 0;

    const { entryValue } = this.state;

    return Number(removeCurrencyFormat(entryValue));
  }

  /* retorna o valor máximo que o usuário pode
  inserir no campo de entrada do financiamento */
  entryRangeInputMaxValue() {
    if (this.usedModelValueBigger()) return 0;

    const { modelValue, usedModelValue } = this.state;
    const numberModelValue = currencyStringToNumber(modelValue);
    const numberUsedModelValue = currencyStringToNumber(usedModelValue);
    const discountPercentage = 0.05;

    const modelValueWithDiscount =
      numberModelValue - numberModelValue * discountPercentage;

    if (usedModelValue) {
      const valueToFund = modelValueWithDiscount - numberUsedModelValue;

      return valueToFund;
    }

    return modelValueWithDiscount;
  }

  rangeInputMaxValueLabel() {
    const value = this.entryRangeInputMaxValue();

    return `R$ ${value.toLocaleString('pt-br', {
      minimumFractionDigits: 2,
    })}`;
  }

  render() {
    const {
      model,
      versionName,
      unit,
      modelThumb,
      modelValue,
      usedOnChange,
      usedModelValue,
      financingValue,
      financingSimulationError,
      financingSimulationIsLoading,
      entryValueIsValid,
      financingValueError,
      installmentsList,
      installments,
    } = this.state;

    return (
      <div className="financing-simulation">
        <h2 className="step__title">Simulações de financiamento</h2>

        <div className="row">
          <div className="col-lg-8 col-md-12">
            <div className="form-group">
              <label
                className="bold-label entry-value-label"
                htmlFor="entry-value"
              >
                Informe o valor de entrada:
                <Tooltip
                  message="Quantia a ser paga à vista pelo comprador ao fechar o contrato de financiamento."
                  mobileDirection="left"
                >
                  <i className="icon icon-info" />
                </Tooltip>
              </label>

              <RangeInput
                value={this.entryRangeInputValue()}
                max={this.entryRangeInputMaxValue()}
                onInput={({ target }) =>
                  this.handleEntryValueChange(target.value)
                }
                onChange={({ target }) =>
                  this.handleEntryValueChange(target.value)
                }
              />
              <div className="range-input__bottom-price-range">
                <div>R$ 0,00</div>
                <div>{this.rangeInputMaxValueLabel()}</div>
              </div>

              <input
                onChange={({ target }) =>
                  this.handleEntryValueChange(target.value)
                }
                value={this.entryInputValue()}
                type="text"
                className="form-control"
                name="entry-value"
                required
                placeholder="Ex: R$ 35.000"
                data-bouncer-target="#invalid-entry-value"
                ref={entryValueInput =>
                  (this.entryValueInput = entryValueInput)
                }
                disabled={this.entryInputDisabled()}
              />

              {!entryValueIsValid && (
                <div
                  id="invalid-entry-value"
                  className="invalid-feedback is-invalid-entry-value"
                >
                  <div className="error-message" id="bouncer-error_entry-value">
                    Por favor, informe esse campo
                  </div>
                </div>
              )}
            </div>

            {usedOnChange === 'sim' && (
              <div className="form-group">
                <label className="bold-label" htmlFor="used-model-value">
                  Valor do seminovo para negociação:
                </label>
                <input
                  onChange={() => {}}
                  value={`R$ ${usedModelValue || '0,00'}`}
                  type="text"
                  className="form-control"
                  name="used-model-value"
                  placeholder="Ex: R$ 50.000"
                  data-bouncer-target="#invalid-used-model-value"
                  readOnly
                />
              </div>
            )}

            <div className="total-values form-group">
              {usedOnChange === 'sim' && (
                <div className="total-values__element">
                  <label className="bold-label" htmlFor="financing-total-value">
                    Valor total da entrada:
                    <Tooltip
                      message="Soma dos valores preenchidos em 'Valor de entrada' e 'Valor do seminovo para negociação'."
                      mobileDirection="left"
                    >
                      <i className="icon icon-info" />
                    </Tooltip>
                  </label>
                  <div className="financing-total-value-box">
                    <h5>R$ {this.getTotalEntryValue()}</h5>
                  </div>
                </div>
              )}

              <div className="total-values__element">
                <label className="bold-label" htmlFor="financing-total-value">
                  Valor total do financiamento:
                </label>
                <div className="financing-total-value-box">
                  <h5>
                    R${' '}
                    {financingValue
                      ? this.maskedValue(financingValue)
                      : modelValue}
                  </h5>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="bold-label">
                Em quantas parcelas deseja pagar?
              </label>

              <div className="financing-terms">
                {installmentsList &&
                  installmentsList.map(({ Prazo }) => (
                    <button
                      type="button"
                      className={`btn financing-term ${
                        Prazo === installments && 'active'
                      }`}
                      onClick={() => {
                        this.handleInstallmentsChange(Prazo);
                      }}
                    >
                      {Prazo}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            <div className="financing-simulation__card">
              <ItemCard
                modelName={model}
                version={versionName}
                unit={unit}
                thumb={modelThumb}
                thumbAlt={model}
                textPrice="Valor do modelo"
                price={modelValue}
              />
            </div>
          </div>

          <div className="col-lg-8 col-md-12">
            <div className="btns-group">
              <button
                type="button"
                className="btn btn-prev btn-prev--outline font-weight-bold"
                data-jump-to-personal-information={`${usedOnChange}`}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn button--primary font-weight-bold"
                onClick={this.simulateFinancing}
                ref={nextButton => (this.nextButton = nextButton)}
              >
                Ver resultado
              </button>
            </div>
          </div>

          {financingSimulationIsLoading && (
            <div className="col-lg-8 col-md-12">
              <div className="financing-simulation__load-box">
                <div className="financing-simulation__load-box-content">
                  <div>
                    <div className="spinner" role="status" />
                  </div>
                  <h5>Simulando financiamento...</h5>
                </div>
              </div>
            </div>
          )}

          {financingValueError && (
            <div className="col-lg-8 col-md-12">
              <div className="financing-simulation__user-error-box">
                <i className="icon icon-alert-circle" />
                <h5>
                  Os valores de entrada informados superam o valor total do
                  veículo. Por favor, informe valores válidos.
                </h5>
              </div>
            </div>
          )}

          {financingSimulationError && (
            <div className="col-lg-8 col-md-12">
              <div className="financing-simulation__error-box">
                <div className="financing-simulation__error-box-content">
                  <h5>
                    Erro de servidor. Por favor, recarregue ou tente novamente
                    mais tarde.
                  </h5>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

FinancingSimulation.defaultProps = {
  category: '',
  brand: '',
  channel: '',
};

FinancingSimulation.propTypes = {
  category: PropTypes.string,
  brand: PropTypes.string,
  channel: PropTypes.string,
};
